import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the tooltip re-init on each draw, the two select2 filters,
// the search input, and the delete-confirmation modal wiring to window.ltAutomationService.
//
// The search input is bound here on "keyup" rather than through the shared debounced
// `datatable-search` binding: this page has always searched instantly, and reusing the shared
// debounce would add a 500ms delay that never existed.
//
// Also mounts on the empty-automations state, where there is no table, no filters and no search
// box; the `hasXTarget` guards keep every method a no-op there.
export default class extends Controller {
  static get targets() {
    return ["table", "search", "select2Filter"];
  }

  connect() {
    window.ltAutomationService.setupEventListeners("index");

    if (!this.hasTableTarget) { return; }

    this.onDraw = this.onDraw.bind(this);
    $(this.tableTarget).on("draw.dt.automations", this.onDraw);
    // The initial ajax-driven draw can land before or after this connect() call finishes wiring
    // the listener above -- same race noted in yardi_properties_controller.js -- so also run it
    // once directly in case a draw already happened.
    this.onDraw();

    this.bindSelect2Filters();

    $(this.element).on("change.automations", ".datatable-moveable-filter", () => this.reload());
  }

  disconnect() {
    if (!this.hasTableTarget) { return; }

    $(this.tableTarget).off(".automations");
    $(this.element).off(".automations");

    this.select2FilterTargets.forEach((el) => {
      const $select = $(el);
      $select.off(".automations");
      if ($select.hasClass("select2-hidden-accessible")) {
        $select.select2("destroy");
      }
    });
  }

  // The page-specific half of the draw callback; the shared `datatable` controller already
  // covers the pagination and highlight reskin. Scoped to this.element.
  onDraw() {
    $(this.element).find("[data-toggle='tooltip']").tooltip();
  }

  // Wired via data-action in the view instead of a manual jQuery binding -- Stimulus's own
  // connect/disconnect lifecycle already makes this idempotent, so the old off-then-on guard
  // and the hasSearchTarget check (search only renders in the same branch as the table) are
  // no longer needed.
  onSearchKeyup(event) {
    $(this.tableTarget).DataTable().search(event.currentTarget.value).draw();
  }

  bindSelect2Filters() {
    this.select2FilterTargets.forEach((el) => {
      const $select = $(el);

      $select.select2({ placeholder: $select.data("placeholder") || "", width: "100%" });
      $select.on("select2:select.automations", (event) => this.markSelect2Filled(event));
      $select.on("select2:unselect.automations", (event) => this.markSelect2Unfilled(event));
    });
  }

  // select2-container--filled is a shared utility class (also used by campaigns and
  // custom_packaging_bundles) styled outside this controller, and it lands on select2's own
  // wrapper element rather than this controller's mount, so it stays a plain classList string,
  // not a Classes API entry.
  markSelect2Filled(event) {
    event.currentTarget.parentElement.querySelector(".select2")?.classList.add("select2-container--filled");
  }

  markSelect2Unfilled(event) {
    event.currentTarget.parentElement.querySelector(".select2")?.classList.remove("select2-container--filled");
  }

  deleteAutomation(event) {
    window.ltAutomationService.deleteAutomation(event);
  }

  // Scoped to this.tableTarget, already unique per mount. This view renders no
  // `.datatable-text-filter`, so that branch is omitted rather than carried dead.
  reload(extraParams = {}) {
    ProgressBar.start();

    const filterSelects = this.element.querySelectorAll(".datatable-moveable-filter, .datatable-filter");
    if (filterSelects.length > 0) {
      const filter = {};
      filterSelects.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "");
        // select2's own data API, not a plain DOM read -- has to stay jQuery-wrapped.
        filter[key] = ($(el).select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
